import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ShieldAlert, AlertTriangle, CalendarClock, Search, Mail, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";

const DOC_LABEL = {
  driver_license: "DMV License",
  tlc_driver_license: "TLC License",
  tlc_driver_suspension: "TLC Suspension",
  vehicle_registration: "Registration",
  vehicle_insurance: "Insurance",
  tlc_vehicle_license: "TLC License",
  tlc_vehicle_suspension: "TLC Suspension",
};

function daysBetween(isoDate) {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return null;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const e = new Date(y, m - 1, d);
  return Math.floor((e - t) / (1000 * 60 * 60 * 24));
}

// Urgency tier: "red" (suspended/expired), "yellow" (≤7d), "orange" (≤30d)
function urgencyOf(item) {
  if (item.status === "suspended" || item.status === "expired") return "red";
  const d = daysBetween(item.expiry_date);
  if (d === null) return "orange";
  if (d <= 7) return "yellow";
  return "orange";
}

function UrgencyDot({ tier }) {
  const cls =
    tier === "red" ? "bg-red-500/80" :
    tier === "yellow" ? "bg-amber-400/80" :
    "bg-orange-400/80";
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${cls}`} />;
}

function StatusBadge({ item }) {
  if (item.status === "suspended") return <Badge className="bg-red-500/15 text-red-400 border-red-500/30 text-[10px]">Suspended</Badge>;
  if (item.status === "expired") return <Badge className="bg-red-500/15 text-red-400 border-red-500/30 text-[10px]">Expired</Badge>;
  return <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px]">Expiring</Badge>;
}

function KpiCard({ label, count, icon: Icon, active, onClick, color, testId }) {
  const accent = {
    red: { bg: "bg-red-500/10", fg: "text-red-400", ring: "ring-red-500/40" },
    yellow: { bg: "bg-amber-500/10", fg: "text-amber-400", ring: "ring-amber-400/40" },
    orange: { bg: "bg-orange-500/10", fg: "text-orange-400", ring: "ring-orange-400/40" },
  }[color];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left w-full rounded-lg border border-border bg-card transition-all hover:bg-muted/10 ${active ? `ring-2 ${accent.ring}` : ""}`}
    >
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${accent.bg}`}>
          <Icon className={`w-5 h-5 ${accent.fg}`} />
        </div>
        <div>
          <p className="text-2xl font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>{count}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </button>
  );
}

function timeAgo(iso) {
  if (!iso) return "Never";
  const then = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function RemindDialog({ open, onClose, targets, onSend, working }) {
  const [email, setEmail] = useState(true);
  const [sms, setSms] = useState(true);

  const method = email && sms ? "both" : email ? "email" : sms ? "sms" : null;
  const disabled = !method || working || targets.length === 0;

  const handleSend = () => onSend(method);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="remind-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Manrope', sans-serif" }}>Send Renewal Reminder</DialogTitle>
          <DialogDescription>
            {targets.length === 1
              ? <>Reminding <span className="text-foreground font-medium">{targets[0].name}</span> about their licensing items.</>
              : <>Reminding <span className="text-foreground font-medium">{targets.length} driver{targets.length !== 1 ? "s" : ""}</span> about their licensing items. Drivers reminded in the last 7 days will be skipped.</>
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <label className="flex items-center gap-3 p-3 rounded-md border border-border bg-muted/10 cursor-pointer">
            <Switch checked={email} onCheckedChange={setEmail} data-testid="remind-email-switch" />
            <Mail className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm">Email (Resend)</span>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-md border border-border bg-muted/10 cursor-pointer">
            <Switch checked={sms} onCheckedChange={setSms} data-testid="remind-sms-switch" />
            <MessageSquare className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm">SMS (Twilio)</span>
          </label>
          {!method && (
            <p className="text-xs text-amber-400">Select at least one channel.</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="remind-cancel-btn">Cancel</Button>
          <Button onClick={handleSend} disabled={disabled} data-testid="remind-send-btn">
            <Send className="w-4 h-4 mr-1.5" />
            {working ? "Sending..." : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LicensingPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("30");
  const [tab, setTab] = useState("action");
  const [kpiFilter, setKpiFilter] = useState(null); // "red" | "yellow" | "orange" | null
  const [searchQuery, setSearchQuery] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [sync, setSync] = useState({ last_driver_sync: null, last_vehicle_sync: null });
  const [preview, setPreview] = useState([]);
  const [remindOpen, setRemindOpen] = useState(false);
  const [remindTargets, setRemindTargets] = useState([]);
  const [remindWorking, setRemindWorking] = useState(false);

  useEffect(() => { loadData(); }, [days, includeArchived]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadSync(); loadPreview(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/licensing/expiring?days=${days}&include_archived=${includeArchived}`);
      setItems(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const loadSync = async () => {
    try {
      const res = await api.get(`/licensing/sync-status`);
      setSync(res.data);
    } catch (e) { console.warn("Load sync status:", e); }
  };

  const loadPreview = async () => {
    try {
      const res = await api.get(`/licensing/remind-preview`);
      setPreview(res.data);
    } catch (e) { console.warn("Load remind preview:", e); }
  };

  const previewMap = useMemo(() => {
    const m = {};
    preview.forEach((p) => { m[p.driver_id] = p; });
    return m;
  }, [preview]);

  const enriched = useMemo(() => items.map((i) => ({
    ...i,
    urgency: urgencyOf(i),
    days_left: daysBetween(i.expiry_date),
  })), [items]);

  const kpiCounts = useMemo(() => ({
    red: enriched.filter((i) => i.urgency === "red").length,
    yellow: enriched.filter((i) => i.urgency === "yellow").length,
    orange: enriched.filter((i) => i.urgency === "orange").length,
  }), [enriched]);

  const filtered = useMemo(() => {
    let list = enriched;
    if (tab === "drivers") list = list.filter((i) => i.entity_kind === "driver");
    else if (tab === "vehicles") list = list.filter((i) => i.entity_kind === "vehicle");
    if (kpiFilter) list = list.filter((i) => i.urgency === kpiFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((i) =>
        i.entity_name?.toLowerCase().includes(q) ||
        i.identifier?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [enriched, tab, kpiFilter, searchQuery]);

  // Unique driver rows shown in the current filtered table — used by "Remind All Visible"
  const visibleDriverTargets = useMemo(() => {
    const seen = new Set();
    const targets = [];
    filtered.forEach((i) => {
      if (i.entity_kind !== "driver") return;
      if (seen.has(i.entity_id)) return;
      const p = previewMap[i.entity_id];
      if (!p || (!p.has_email && !p.has_phone)) return;
      seen.add(i.entity_id);
      targets.push({ driver_id: i.entity_id, name: i.entity_name });
    });
    return targets;
  }, [filtered, previewMap]);

  const openRemindSingle = (item) => {
    if (item.entity_kind !== "driver") return;
    const p = previewMap[item.entity_id];
    if (!p) { toast.error("No reminder items for this driver"); return; }
    if (!p.has_email && !p.has_phone) { toast.error("Driver has no email or phone on file"); return; }
    setRemindTargets([{ driver_id: item.entity_id, name: item.entity_name }]);
    setRemindOpen(true);
  };

  const openRemindAll = () => {
    if (visibleDriverTargets.length === 0) { toast.error("No drivers with contact info in the visible list"); return; }
    setRemindTargets(visibleDriverTargets);
    setRemindOpen(true);
  };

  const handleRemindSend = async (method) => {
    setRemindWorking(true);
    try {
      const driver_ids = remindTargets.map((t) => t.driver_id);
      const res = await api.post(`/licensing/remind`, { driver_ids, method });
      const { sent, skipped, errors } = res.data;
      if (sent > 0) toast.success(`${sent} reminder${sent !== 1 ? "s" : ""} sent`);
      if (skipped > 0) toast.message(`${skipped} skipped (cooldown / no contact info)`);
      if (errors > 0) toast.error(`${errors} error${errors !== 1 ? "s" : ""}`);
      if (sent === 0 && skipped === 0 && errors === 0) toast.message("Nothing to send");
      setRemindOpen(false);
      loadPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reminders");
    } finally {
      setRemindWorking(false);
    }
  };

  const handleRowClick = (item) => {
    if (item.entity_kind === "driver") navigate(`/drivers?open=${item.entity_id}`);
    else navigate(`/vehicles?open=${item.entity_id}`);
  };

  return (
    <div data-testid="licensing-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl tracking-tight font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Licensing & Compliance
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Driver and vehicle license monitoring</p>
        </div>
        <Button
          onClick={openRemindAll}
          disabled={visibleDriverTargets.length === 0}
          className="bg-primary/20 text-primary border border-primary/40 hover:bg-primary/30"
          data-testid="remind-all-btn"
        >
          <Send className="w-4 h-4 mr-1.5" />
          Remind {visibleDriverTargets.length > 0 ? `${visibleDriverTargets.length} Driver${visibleDriverTargets.length !== 1 ? "s" : ""}` : "Drivers"}
        </Button>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          label="Suspended / Expired"
          count={kpiCounts.red}
          icon={ShieldAlert}
          color="red"
          active={kpiFilter === "red"}
          onClick={() => setKpiFilter(kpiFilter === "red" ? null : "red")}
          testId="kpi-red"
        />
        <KpiCard
          label="Expiring ≤ 7 days"
          count={kpiCounts.yellow}
          icon={AlertTriangle}
          color="yellow"
          active={kpiFilter === "yellow"}
          onClick={() => setKpiFilter(kpiFilter === "yellow" ? null : "yellow")}
          testId="kpi-yellow"
        />
        <KpiCard
          label="Expiring ≤ 30 days"
          count={kpiCounts.orange}
          icon={CalendarClock}
          color="orange"
          active={kpiFilter === "orange"}
          onClick={() => setKpiFilter(kpiFilter === "orange" ? null : "orange")}
          testId="kpi-orange"
        />
      </div>

      {/* Sticky filter bar */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border border-border rounded-lg p-3 flex items-center gap-3 flex-wrap">
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setKpiFilter(null); }}>
          <TabsList data-testid="licensing-tabs">
            <TabsTrigger value="action" data-testid="tab-action">Action Required</TabsTrigger>
            <TabsTrigger value="drivers" data-testid="tab-drivers">Drivers</TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="tab-vehicles">Vehicles</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-1" data-testid="days-pills">
          {["7", "14", "30"].map((d) => (
            <Button
              key={d}
              size="sm"
              variant={days === d ? "default" : "outline"}
              onClick={() => setDays(d)}
              className="h-8 px-3 text-xs"
              data-testid={`days-pill-${d}`}
            >
              {d}d
            </Button>
          ))}
        </div>

        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search name, plate, driver #..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-card border-border h-8 text-sm"
            data-testid="licensing-search"
          />
        </div>

        <div className="flex items-center gap-2" data-testid="include-archived-toggle">
          <Switch
            checked={includeArchived}
            onCheckedChange={setIncludeArchived}
            data-testid="include-archived-switch"
          />
          <span className="text-xs text-muted-foreground">Include archived</span>
        </div>

        <div className="flex-1" />

        <div className="text-[10px] text-muted-foreground/70 text-right leading-tight" data-testid="sync-status">
          <div>Drivers synced <span className="text-muted-foreground">{timeAgo(sync.last_driver_sync)}</span></div>
          <div>Vehicles synced <span className="text-muted-foreground">{timeAgo(sync.last_vehicle_sync)}</span></div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/20 hover:bg-muted/20">
              <TableHead className="w-[40px]"></TableHead>
              <TableHead>Name / Plate</TableHead>
              <TableHead>Document</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead className="text-center">Days Left</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[90px] text-right">Remind</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                No items to flag in this range. All clear.
              </TableCell></TableRow>
            ) : (
              filtered.map((item, i) => {
                const p = previewMap[item.entity_id];
                const canRemind = item.entity_kind === "driver" && p && (p.has_email || p.has_phone);
                const lastSent = p?.last_reminder_sent_at;
                return (
                  <TableRow
                    key={`${item.entity_id}-${item.type}-${i}`}
                    className="cursor-pointer hover:bg-muted/20 transition-colors"
                    onClick={() => handleRowClick(item)}
                    data-testid={`licensing-row-${item.entity_id}-${item.type}`}
                  >
                    <TableCell><UrgencyDot tier={item.urgency} /></TableCell>
                    <TableCell className="text-sm font-medium">
                      {item.entity_name}
                      {item.identifier && (
                        <span className="ml-2 text-xs text-muted-foreground" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                          {item.entity_kind === "driver" ? `#${item.identifier}` : item.identifier}
                        </span>
                      )}
                      {lastSent && (
                        <span className="ml-2 text-[10px] text-muted-foreground/60" data-testid={`last-reminded-${item.entity_id}`}>
                          · reminded {timeAgo(lastSent)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{DOC_LABEL[item.type] || item.type}</TableCell>
                    <TableCell className="text-sm" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                      {item.expiry_date || "—"}
                    </TableCell>
                    <TableCell className="text-sm text-center" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                      {item.days_left === null ? "—" : item.days_left < 0 ? <span className="text-red-400">{item.days_left}</span> : item.days_left}
                    </TableCell>
                    <TableCell><StatusBadge item={item} /></TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      {canRemind ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openRemindSingle(item)}
                          className="h-7 px-2 text-xs text-primary hover:bg-primary/10"
                          data-testid={`remind-btn-${item.entity_id}`}
                        >
                          <Send className="w-3.5 h-3.5 mr-1" /> Remind
                        </Button>
                      ) : (
                        <span className="text-muted-foreground/30 text-xs">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <RemindDialog
        open={remindOpen}
        onClose={() => setRemindOpen(false)}
        targets={remindTargets}
        onSend={handleRemindSend}
        working={remindWorking}
      />
    </div>
  );
}
